/**
 * ToastProvider — single-source notification shelf.
 *
 * Pure React state, no library. Mounts a portal at `<body>` so toasts
 * sit above any modal stack. The shelf lives in an `aria-live=polite`
 * region so screen readers announce new toasts without interrupting.
 *
 * Usage:
 *   const { push } = useToast();
 *   push({ kind: 'success', text: 'Saved.' });
 *
 * Toasts auto-dismiss after `duration` ms (default 3500). Pressing
 * Escape dismisses the most-recent visible toast. Each toast is a
 * pure value with a stable id so React can key + transition it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  text: string;
  /** Optional second line. */
  detail?: string;
  /** ms; 0 disables auto-dismiss. */
  duration?: number;
}

type Push = (t: Omit<Toast, 'id'>) => string;

interface ToastApi {
  push: Push;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let _seq = 0;
const nextId = () => `t-${Date.now().toString(36)}-${++_seq}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Track timers so we can cancel on manual dismiss.
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((all) => all.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback<Push>(
    (input) => {
      const id = nextId();
      const t: Toast = { id, duration: 3500, ...input };
      setToasts((all) => [...all, t]);
      if (t.duration && t.duration > 0) {
        const handle = window.setTimeout(() => dismiss(id), t.duration);
        timers.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  // ESC dismisses the top-most toast.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setToasts((all) => {
        if (all.length === 0) return all;
        const top = all[all.length - 1];
        const handle = timers.current.get(top.id);
        if (handle !== undefined) {
          window.clearTimeout(handle);
          timers.current.delete(top.id);
        }
        return all.slice(0, -1);
      });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="toast-shelf"
          role="region"
          aria-live="polite"
          aria-label="Notifications"
        >
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

/**
 * Like `useToast`, but returns `null` when no provider is mounted instead of
 * throwing — for surfaces (and their unit tests) where a toast is a nice-to-
 * have signal, not a hard dependency. Prod always mounts the provider.
 */
export function useOptionalToast(): ToastApi | null {
  return useContext(ToastContext);
}

const KIND_GLYPH: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: AlertTriangle,
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Glyph = KIND_GLYPH[toast.kind];
  return (
    <div className={`toast toast--${toast.kind}`} role="status">
      <span className="toast__glyph" aria-hidden="true">
        <Glyph size={14} strokeWidth={2.25} />
      </span>
      <div className="toast__body">
        <div className="toast__text">{toast.text}</div>
        {toast.detail && <div className="toast__detail">{toast.detail}</div>}
      </div>
      <button
        type="button"
        className="toast__close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        <X size={12} />
      </button>
    </div>
  );
}
